import React, { useState, useEffect } from 'react';
import { Box, Typography, Link, CircularProgress } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { getFooterPages } from '../../services/pageService';
import { Page } from '../../types/page';

interface FooterLinksProps {
  section: 'quick_links' | 'information';
  title: string;
}

const FooterLinks: React.FC<FooterLinksProps> = ({ section, title }) => {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPages = async () => {
      try {
        setLoading(true);
        const pagesData = await getFooterPages();
        
        // تصفية الصفحات حسب القسم
        const filteredPages = pagesData.filter(page => 
          page.showInFooter && 
          (page.footerSection === section || (section === 'quick_links' && !page.footerSection))
        );
        
        // ترتيب الصفحات حسب الترتيب
        filteredPages.sort((a, b) => a.order - b.order);
        
        setPages(filteredPages);
        setLoading(false);
      } catch (error) {
        console.error('Error loading footer pages:', error);
        setLoading(false);
      }
    };

    loadPages();
  }, [section]);

  // إضافة روابط افتراضية إذا لم تكن هناك صفحات
  const getDefaultLinks = () => {
    if (section === 'quick_links') {
      return [
        { title: 'الرئيسية', url: '/' },
        { title: 'المنتجات', url: '/products' },
        { title: 'العروض', url: '/offers' },
        { title: 'من نحن', url: '/about' },
        { title: 'اتصل بنا', url: '/contact' }
      ];
    } else {
      return [
        { title: 'سياسة الخصوصية', url: '/privacy-policy' },
        { title: 'شروط الاستخدام', url: '/terms' },
        { title: 'سياسة الإرجاع', url: '/return-policy' },
        { title: 'طرق الدفع', url: '/payment-methods' },
        { title: 'الشحن والتوصيل', url: '/shipping' }
      ];
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  // إذا لم تكن هناك صفحات، استخدم الروابط الافتراضية
  const links = pages.length > 0 ? pages : getDefaultLinks();

  return (
    <Box>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
        {title}
      </Typography>
      <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
        {pages.length > 0 ? (
          // عرض الصفحات من قاعدة البيانات
          pages.map((page) => (
            <Box component="li" key={page.id} sx={{ mb: 1 }}>
              <Link
                component={RouterLink}
                to={`/${page.slug}`}
                color="text.secondary"
                underline="hover"
                sx={{
                  display: 'block',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'translateX(-8px)'
                  }
                }}
              >
                {page.title}
              </Link>
            </Box>
          ))
        ) : (
          // عرض الروابط الافتراضية
          getDefaultLinks().map((link, index) => (
            <Box component="li" key={index} sx={{ mb: 1 }}>
              <Link
                component={RouterLink}
                to={link.url}
                color="text.secondary"
                underline="hover"
                sx={{
                  display: 'block',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: 'primary.main',
                    transform: 'translateX(-8px)'
                  }
                }}
              >
                {link.title}
              </Link>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default FooterLinks;
